import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, ViewChild, AfterViewInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ExclusiveDiscountsService, ExclusiveDiscountItem, ExclusiveDiscounts } from '../../../services/exclusive-discounts.service';
import { AnalyticsService } from '../../../services/analytics.service';

@Component({
  selector: 'app-exclusive-discounts',
  standalone: true,
  imports: [CommonModule, HttpClientModule],
  templateUrl: './exclusive-discounts.component.html',
  styleUrls: ['./exclusive-discounts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExclusiveDiscountsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('sectionElement', { static: false }) sectionElement?: ElementRef;

  discounts: ExclusiveDiscounts = {
    unimarc: [],
    alvi: [],
    mayorista: []
  };

  isLoading = true;
  error = false;
  private destroy$ = new Subject<void>();
  private isInitialized = false; // Protección contra re-inicialización
  public deviceType: 'xl' | 'desktop' | 'tablet' | 'movil';
  private resizeTimeout: any;

  constructor(
    private exclusiveDiscountsService: ExclusiveDiscountsService,
    private cdr: ChangeDetectorRef,
    private analyticsService: AnalyticsService
  ) {
    // Detectar tipo de dispositivo inicial
    this.deviceType = this.exclusiveDiscountsService.getCurrentDeviceType();
  }

  ngOnInit() {
    if (!this.isInitialized) {
      this.loadDiscounts();
      this.isInitialized = true;
    }
  }

  ngAfterViewInit() {
    // Registrar la sección para tracking de visibilidad
    if (this.sectionElement?.nativeElement) {
      this.analyticsService.registerSectionForTracking(
        this.sectionElement.nativeElement,
        'Exclusive Discounts',
        'exclusive-discounts-section'
      );
    }

    // Registrar promociones para lazy loading después de que se rendericen
    setTimeout(() => {
      this.registerPromotionsForTracking();
    }, 100);
  }

  ngOnDestroy() {
    // Limpiar timeout de resize
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    // Limpiar el tracking de la sección
    if (this.sectionElement?.nativeElement) {
      this.analyticsService.unregisterSectionFromTracking(this.sectionElement.nativeElement);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Listener para cambios de tamaño de ventana
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    // Debounce para evitar múltiples llamadas durante el resize
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      // Obtener el nuevo tipo de dispositivo
      const newDeviceType = this.exclusiveDiscountsService.getCurrentDeviceType();

      // Solo recargar si cambió el tipo de dispositivo
      if (newDeviceType !== this.deviceType) {
        console.log(`[ExclusiveDiscounts] Dispositivo cambiado de ${this.deviceType} a ${newDeviceType}`);
        this.deviceType = newDeviceType;
        this.loadDiscounts();
      }
    }, 300); // Esperar 300ms después del último resize
  }

  loadDiscounts() {
    console.log('🔄 Exclusive Discounts: Iniciando carga de descuentos');
    console.log('📱 Tipo de dispositivo detectado:', this.deviceType);

    this.isLoading = true;
    this.error = false;
    this.cdr.markForCheck();

    this.exclusiveDiscountsService.getAllDiscounts(this.deviceType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (discounts) => {
          console.log('✅ Exclusive Discounts: Descuentos cargados exitosamente');
          console.log('📋 Unimarc:', discounts.unimarc.length, 'items');
          console.log('📋 Alvi:', discounts.alvi.length, 'items');
          console.log('📋 Mayorista 10:', discounts.mayorista.length, 'items');

          this.discounts = discounts;
          this.isLoading = false;
          this.cdr.markForCheck();

          // Registrar promociones después de cargar los datos
          setTimeout(() => {
            this.registerPromotionsForTracking();
          }, 100);
        },
        error: (error) => {
          console.error('❌ Exclusive Discounts: Error cargando descuentos:', error);
          this.error = true;
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  // Método para registrar todas las promociones para tracking
  private registerPromotionsForTracking(): void {
    // Registrar promociones de Unimarc
    this.discounts.unimarc.forEach((discount, index) => {
      const elementId = `unimarc-${discount.id}`;
      const element = document.getElementById(elementId);
      if (element) {
        this.analyticsService.registerPromotionForTracking(
          element,
          elementId,
          discount.titulo || '',
          discount.img || '',
          `exclusive_unimarc_${index + 1}`
        );
      }
    });

    // Registrar promociones de Alvi
    this.discounts.alvi.forEach((discount, index) => {
      const elementId = `alvi-${discount.id}`;
      const element = document.getElementById(elementId);
      if (element) {
        this.analyticsService.registerPromotionForTracking(
          element,
          elementId,
          discount.titulo || '',
          discount.img || '',
          `exclusive_alvi_${index + 1}`
        );
      }
    });

    // Registrar promociones de Mayorista
    this.discounts.mayorista.forEach((discount, index) => {
      const elementId = `mayorista-${discount.id}`;
      const element = document.getElementById(elementId);
      if (element) {
        this.analyticsService.registerPromotionForTracking(
          element,
          elementId,
          discount.titulo || '',
          discount.img || '',
          `exclusive_mayorista_${index + 1}`
        );
      }
    });
  }

  // Método para forzar actualización
  refreshDiscounts() {
    console.log('🔄 Exclusive Discounts: Forzando actualización');
    this.exclusiveDiscountsService.forceRefresh(this.deviceType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (discounts) => {
          console.log('✅ Exclusive Discounts: Descuentos actualizados');
          this.discounts = discounts;
          this.cdr.markForCheck();

          // Re-registrar promociones después de actualizar
          setTimeout(() => {
            this.registerPromotionsForTracking();
          }, 100);
        },
        error: (error) => {
          console.error('❌ Exclusive Discounts: Error actualizando descuentos:', error);
        }
      });
  }

  // Método para obtener estadísticas
  getDiscountsStatistics() {
    return this.exclusiveDiscountsService.getDiscountsStatistics();
  }

  // Método para debugging - verificar estado del servicio
  getServiceStatus() {
    return this.exclusiveDiscountsService.getCacheStatus();
  }

  // Método para obtener el tipo de dispositivo actual
  getCurrentDeviceType(): string {
    return this.exclusiveDiscountsService.getCurrentDeviceType();
  }

  // Método para obtener el ancho actual de la ventana (para debugging)
  getCurrentWidth(): number {
    return window.innerWidth;
  }

  // Método para trackear items en el template
  trackByDiscountId(index: number, item: ExclusiveDiscountItem): number {
    return item.id;
  }

  // Método para manejar errores de imagen
  onImageError(event: Event, brand: string, index: number): void {
    const img = event.target as HTMLImageElement;

    // Aquí podrías establecer una imagen por defecto si es necesario
    img.src = '';
  }

  // Método para abrir descuentos
  openDiscount(url: string): void {
    if (url && url !== '') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  // Método para trackear click en promoción
  onPromotionClick(discount: ExclusiveDiscountItem, brand: string, index: number): void {
    const promotionId = `${brand}-${discount.id}`;
    const promotionName = discount.titulo || '';
    const creativeName = discount.img || '';
    const creativeSlot = `exclusive_${brand}_${index + 1}`;
    const deviceType = this.getCurrentDeviceType();

    const eventData = this.analyticsService.createPromotionEventData(
      promotionId,
      deviceType,
      promotionName,
      creativeName,
      creativeSlot
    );

    this.analyticsService.trackPromotionSelection(eventData);

    // También abrir el enlace
    this.openDiscount(discount.url || '');
  }
}
